/**
 * ss-cartix.js — Shared Sweeps × Cartix cart drawer + Kaching bundles
 *
 * Cartix drawer:
 *   • ss-cart-progress below .cd-cart-header
 *   • shared-sweeps banner as a full-width row at the bottom of each cart line
 *   • shared-sweeps banner as a full-width row at the bottom of each "Customers also purchased" upsell
 *   • Total Entries (from ss-entries-total) under the subtotal, styled as the subtotal
 *
 * Kaching bundles (PDP):
 *   • entries multiplier badge beside each deal-bar title
 *
 * All markup and math come from Liquid via the Section Rendering API
 * (sections/ss-cart-sweeps.liquid, sections/ss-product-sweeps.liquid).
 */
(() => {
  'use strict';

  if (window.__ssCartixLoaded) return;
  window.__ssCartixLoaded = true;

  const CONFIG = {
    appRoot: '#cart-drawer-app',
    drawer: '.cd-cart-inner', // nothing is fetched until Cartix has rendered its drawer
    sections: { cart: 'ss-cart-sweeps', product: 'ss-product-sweeps' },
    progress: {
      enabled: true,
      // Scoped to .cd-cart-inner — Cartix repeats .cd-cart-header in its upsell-detail pane.
      target: '.cd-cart-inner > .cd-cart-header',
      position: 'afterend',
    },
    line: {
      item: '.cd-cart-item',
      prices: '.cd-cart-item-prices',
      target: null,           // null = the .cd-cart-item itself
      position: 'beforeend',  // last child → bottom of the line item
    },
    upsell: {
      item: '.cd-cart-upsell-item',
      link: 'a[href*="/products/"]',
      target: null,           // null = the .cd-cart-upsell-item itself
      position: 'beforeend',  // last child → below everything in the upsell item
    },
    total: {
      enabled: true,
      // :not(.ss-cartix-slot) keeps these pointed at Cartix's real subtotal, not our clone.
      target: '.cd-cart-subtotal:not(.ss-cartix-slot)',
      position: 'afterend',
      label: 'Total Entries:',
      rowClass: 'cd-cart-subtotal',
      amountClass: 'cd-cart-subtotal-amount',
    },
    kaching: {
      enabled: true,
      block: 'kaching-bundles-block',                  // carries deal-block + product JSON
      bar: '.kaching-bundles__bar[data-deal-bar-id]',
      target: '.kaching-bundles__bar-title',
      position: 'afterend',                            // right after the title
      multipliers: { 1: 1, 2: 2, 3: 3, 5: 5 },         // bundle quantity → entries multiplier
      label: (m) => `${m}X Entries`,
    },
    subtotalAmount: '.cd-cart-subtotal:not(.ss-cartix-slot) .cd-cart-subtotal-amount',
    debounceMs: 120,
    staleRetry: { attempts: 3, delayMs: 400 },
    productCacheTtlMs: 15 * 60 * 1000,
  };

  const ROOT_URL = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CURRENCY = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
  const SLOT = 'ss-cartix-slot';
  const SNIPPET_STYLE_ID = 'ss-banner-styles';
  const CACHE_PREFIX = 'ss-cartix:v2:';
  const EMPTY_CART = { lines: [], total_entries: 0, progress: '', byKey: {}, byLine: {} };

  const state = {
    cartSig: null,
    cartData: EMPTY_CART,
    cartVersion: 0,
    products: new Map(),
  };

  /* ---------- helpers ---------- */

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');

  function parseHTML(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    return tpl.content;
  }

  // Section Rendering API wraps output in <div class="shopify-section">; unwrap it.
  function sectionInner(html) {
    const wrap = parseHTML(html).querySelector('.shopify-section');
    return wrap ? wrap.innerHTML : html;
  }

  // shared-sweeps ships a <style> block with every render — keep one copy in <head>, strip the rest.
  // Only used for shared-sweeps banner HTML (not the progress bar).
  function prepare(html) {
    if (!html || !html.trim()) return '';
    const frag = parseHTML(html);
    frag.querySelectorAll('style').forEach((style) => {
      if (!document.getElementById(SNIPPET_STYLE_ID)) {
        style.id = SNIPPET_STYLE_ID;
        document.head.appendChild(style);
      } else {
        style.remove();
      }
    });
    const box = document.createElement('div');
    box.appendChild(frag);
    return box.innerHTML.trim();
  }

  // innerHTML never executes <script>; re-create them so a snippet's own init runs.
  function runScripts(container) {
    container.querySelectorAll('script').forEach((old) => {
      const script = document.createElement('script');
      [...old.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  }

  async function fetchText(url) {
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error(`${res.status} ${url}`);
    return res.text();
  }

  /* ---------- slots (our injected containers) ---------- */

  function findSlot(target, position, type) {
    const cls = `${SLOT}--${type}`;
    let el = null;
    if (position === 'beforeend' || position === 'afterbegin') el = target.querySelector(`:scope > .${cls}`);
    else if (position === 'afterend') el = target.nextElementSibling;
    else if (position === 'beforebegin') el = target.previousElementSibling;
    return el && el.classList.contains(cls) ? el : null;
  }

  // The stamp prevents rewriting unchanged slots, which keeps the MutationObservers from looping.
  function upsertSlot(target, position, type, stamp, html) {
    let slot = findSlot(target, position, type);
    if (slot && slot.dataset.ssStamp === stamp) return slot;
    if (!slot) {
      slot = document.createElement('div');
      slot.className = `${SLOT} ${SLOT}--${type}`;
      target.insertAdjacentElement(position, slot);
    }
    slot.dataset.ssStamp = stamp;
    slot.innerHTML = html;
    return slot;
  }

  /* ---------- cart data ---------- */

  const lineKey = (item) => item.querySelector('[data-cart-item]')?.getAttribute('data-cart-item') || null;
  const lineIndex = (item) => item.querySelector('[data-cart-item-line]')?.getAttribute('data-cart-item-line') || null;

  function lineQty(item) {
    const el = item.querySelector('[data-cart-quantity]');
    const raw = el ? el.getAttribute('data-cart-quantity') : item.querySelector('input')?.value;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  function findLine(data, item) {
    return data.byKey[lineKey(item)] || data.byLine[lineIndex(item)] || null;
  }

  function priceText(item) {
    const box = item.querySelector(CONFIG.line.prices);
    if (!box) return '';
    return [...box.childNodes]
      .filter((n) => !(n.nodeType === 1 && n.classList.contains(SLOT)))
      .map((n) => n.textContent.trim())
      .join(' ');
  }

  // Changes whenever Cartix shows a different cart (lines, quantities, prices, subtotal).
  function cartSignature(root, items) {
    const lines = items.map((item) => `${lineKey(item)}~${lineQty(item)}~${priceText(item)}`);
    const subtotal = root.querySelector(CONFIG.subtotalAmount)?.textContent.trim() || '';
    return `${lines.join('|')}#${subtotal}`;
  }

  async function fetchCartData() {
    const html = await fetchText(`${ROOT_URL}cart?section_id=${CONFIG.sections.cart}`);
    const script = parseHTML(html).querySelector('script[data-ss-cart-sweeps]');
    if (!script) throw new Error(`section "${CONFIG.sections.cart}" not found`);
    const data = JSON.parse(script.textContent);
    data.progress = data.progress || '';
    data.byKey = {};
    data.byLine = {};
    data.lines.forEach((line) => {
      line.html = prepare(line.html);
      data.byKey[line.key] = line;
      data.byLine[line.line] = line;
    });
    return data;
  }

  // Cartix may update the DOM before /cart/change.js settles — refetch until quantities match.
  async function fetchCartMatching(items) {
    let data;
    for (let i = 0; i < CONFIG.staleRetry.attempts; i++) {
      if (i) await sleep(CONFIG.staleRetry.delayMs);
      data = await fetchCartData();
      const inSync = items.every((item) => {
        const line = findLine(data, item);
        const qty = lineQty(item);
        return line && (qty === null || line.quantity === qty);
      });
      if (inSync) break;
    }
    return data;
  }

  /* ---------- cart lines ---------- */

  async function syncLines(root) {
    const items = [...root.querySelectorAll(CONFIG.line.item)];
    const sig = cartSignature(root, items);

    if (sig !== state.cartSig) {
      // Fetched even for an empty cart so the progress bar still renders.
      state.cartData = await fetchCartMatching(items);
      state.cartSig = sig;
      state.cartVersion += 1;
    }

    items.forEach((item) => {
      const target = CONFIG.line.target ? item.querySelector(CONFIG.line.target) : item;
      if (!target) return;
      const line = findLine(state.cartData, item);
      upsertSlot(
        target,
        CONFIG.line.position,
        'line',
        `${state.cartVersion}:${lineKey(item)}`,
        line ? line.html : ''
      );
    });
  }

  /* ---------- product banners (upsells + Kaching eligibility) ---------- */

  function productPath(href) {
    try {
      const url = new URL(href, location.origin);
      if (url.origin !== location.origin) return null;
      const match = url.pathname.match(/^(.*\/products\/[^/?#]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  function cacheRead(key) {
    try {
      const hit = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + key));
      return hit && Date.now() - hit.t < CONFIG.productCacheTtlMs ? hit.html : null;
    } catch (e) {
      return null;
    }
  }

  function cacheWrite(key, html) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), html }));
    } catch (e) { /* storage full or blocked — memory cache still works */ }
  }

  function loadProduct(path) {
    const key = `${CURRENCY}:${path}`;
    if (state.products.has(key)) return state.products.get(key);

    const cached = cacheRead(key);
    const source = cached !== null
      ? Promise.resolve(cached)
      : fetchText(`${path}?section_id=${CONFIG.sections.product}`).then((html) => {
          const inner = sectionInner(html).trim();
          cacheWrite(key, inner);
          return inner;
        });

    const promise = source.then(prepare).catch((err) => {
      console.warn('[ss-cartix] product banner failed:', path, err);
      return ''; // stays memoized so a bad product isn't refetched on every render
    });

    state.products.set(key, promise);
    return promise;
  }

  /* ---------- upsells ---------- */

  function syncUpsells(root) {
    root.querySelectorAll(CONFIG.upsell.item).forEach((item) => {
      const path = productPath(item.querySelector(CONFIG.upsell.link)?.href);
      const target = CONFIG.upsell.target ? item.querySelector(CONFIG.upsell.target) : item;
      if (!path || !target) return;

      const slot = findSlot(target, CONFIG.upsell.position, 'upsell');
      if (slot && slot.dataset.ssStamp === path) return;

      loadProduct(path).then((html) => {
        // Cartix may have re-rendered or swapped this upsell while fetching.
        if (!target.isConnected) return;
        if (productPath(item.querySelector(CONFIG.upsell.link)?.href) !== path) return;
        upsertSlot(target, CONFIG.upsell.position, 'upsell', path, html);
      });
    });
  }

  /* ---------- total ---------- */

  function syncTotal(root) {
    if (!CONFIG.total.enabled) return;
    const target = root.querySelector(CONFIG.total.target);
    if (!target) return;

    // ss-entries-total already includes per-product bonuses and the milestone bonus.
    const entries = state.cartData.total_entries || 0;
    const value = entries > 0 ? fmt(entries) : '';

    // Mirrors Cartix's markup: <div class="cd-cart-subtotal">Subtotal: <span class="cd-cart-subtotal-amount">…</span></div>
    const html = value
      ? `${CONFIG.total.label} <span class="${CONFIG.total.amountClass}">${value}</span>`
      : '';

    const slot = upsertSlot(target, CONFIG.total.position, 'total', value, html);

    // Borrow Cartix's subtotal class only while there's something to show,
    // so its display rules can't un-hide an empty row.
    slot.classList.toggle(CONFIG.total.rowClass, !!value);
  }

  /* ---------- progress ---------- */

  function syncProgress(root) {
    if (!CONFIG.progress.enabled) return;
    const target = root.querySelector(CONFIG.progress.target);
    if (!target) return;

    const stamp = String(state.cartVersion);
    const existing = findSlot(target, CONFIG.progress.position, 'progress');
    if (existing && existing.dataset.ssStamp === stamp) return;

    const slot = upsertSlot(target, CONFIG.progress.position, 'progress', stamp, state.cartData.progress || '');

    // Line the bar up with the header's horizontal padding.
    const cs = getComputedStyle(target);
    slot.style.paddingLeft = cs.paddingLeft;
    slot.style.paddingRight = cs.paddingRight;

    // Runs the snippet's inline ssCartProgressInit(); it animates fill + amount
    // from the previous values stored on window.__ssCartProgress.
    runScripts(slot);
  }

  /* ---------- Kaching bundles (PDP) ---------- */

  const kachingDeals = new WeakMap();
  const kachingSkipped = new Set();
  const NUMBER_WORDS = {
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
    seven: 7, eight: 8, nine: 9, ten: 10, twelve: 12,
  };

  function parseAttrJSON(el, name) {
    try {
      return JSON.parse(el.getAttribute(name) || 'null');
    } catch (e) {
      return null;
    }
  }

  // dealBarId → bundle quantity from Kaching's deal-block config. Re-parsed whenever the
  // attribute changes, so a block read before Kaching filled it in can't get stuck empty.
  function kachingQuantities(block) {
    const raw = block.getAttribute('deal-block') || '';
    const hit = kachingDeals.get(block);
    if (hit && hit.raw === raw) return hit.map;

    const map = {};
    try {
      const deal = JSON.parse(raw || 'null');
      (deal && deal.dealBars ? deal.dealBars : []).forEach((bar) => {
        map[bar.id] = Number(bar.quantity) || 0;
      });
    } catch (e) { /* fall back to the bar titles */ }

    kachingDeals.set(block, { raw, map });
    return map;
  }

  // Fallback: "One Grafton" → 1, "Six Graftons" → 6, "2 Graftons" → 2.
  function titleQuantity(text) {
    const match = String(text || '').trim().toLowerCase().match(/^(\d+|[a-z]+)/);
    if (!match) return 0;
    return /^\d+$/.test(match[1]) ? parseInt(match[1], 10) : NUMBER_WORDS[match[1]] || 0;
  }

  // Highest tier <= qty, mirroring the `for tier in ss_bundle_tiers` loop in
  // snippets/ss-bundle-multiplier.liquid (4-5 -> 3x, 6+ -> 5x). An exact-key lookup drops
  // any quantity the deal uses that isn't literally a tier key, and renders no badge at all.
  function kachingMultiplier(qty) {
    const tiers = CONFIG.kaching.multipliers;
    let mult = 0;
    Object.keys(tiers)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((tier) => {
        if (qty >= tier) mult = tiers[tier];
      });
    return mult;
  }

  function kachingProductPath(block) {
    const product = block ? parseAttrJSON(block, 'product') : null;
    if (product && product.handle) return `${ROOT_URL}products/${product.handle}`;
    return productPath(location.href);
  }

  // The ss-product-sweeps render doubles as the eligibility check (empty = not eligible)
  // and carries the theme's --ss-* color variables on its wrapper.
  function sweepsVars(html) {
    if (!html) return null;
    const wrap = parseHTML(html).querySelector('.ss-banner-wrapper');
    return wrap ? (wrap.getAttribute('style') || '').replace(/\s+/g, ' ').trim() : null;
  }

  const escapeAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');

  function syncKaching() {
    const cfg = CONFIG.kaching;

    document.querySelectorAll(cfg.bar).forEach((bar) => {
      const target = bar.querySelector(cfg.target);
      if (!target) return;

      const block = bar.closest(cfg.block);
      const path = kachingProductPath(block);
      if (!path) return;

      loadProduct(path).then((html) => {
        if (!target.isConnected) return;

        const vars = sweepsVars(html);
        if (vars === null) {
          if (!kachingSkipped.has(path)) {
            kachingSkipped.add(path);
            console.info('[ss-cartix] Kaching badges skipped — product not sweeps-eligible:', path);
          }
          return;
        }

        const quantities = block ? kachingQuantities(block) : {};
        const qty = quantities[bar.getAttribute('data-deal-bar-id')] || titleQuantity(target.textContent);
        const mult = kachingMultiplier(qty);
        const badge = mult
          ? `<span class="ss-kaching-badge" style="${escapeAttr(vars)}">${cfg.label(mult)}</span>`
          : '';

        upsertSlot(target, cfg.position, 'kaching', String(mult), badge);
      });
    });
  }

  // Kaching mounts its blocks with JS — often after this script runs — and re-renders bars
  // on selection/variant changes, so watch the page instead of a fixed element.
  function bootKaching() {
    if (!CONFIG.kaching.enabled) return;

    let t = 0;
    // Throttle (not debounce) so constant DOM churn elsewhere on the page can't starve it.
    const scheduleKaching = () => {
      if (t) return;
      t = setTimeout(() => {
        t = 0;
        syncKaching();
      }, CONFIG.debounceMs);
    };

    const mo = new MutationObserver((records) => {
      if (records.every(isOwnMutation)) return;
      scheduleKaching();
    });
    mo.observe(document.body, { childList: true, subtree: true });
    scheduleKaching();
  }

  /* ---------- styles ---------- */

  function injectBaseStyles() {
    if (document.getElementById('ss-cartix-styles')) return;
    const style = document.createElement('style');
    style.id = 'ss-cartix-styles';
    style.textContent = `
      .${SLOT}:empty { display: none; }

      /* Progress: between the header and Cartix's rewards bar */
      .${SLOT}--progress { padding-top: 12px; }

      /* Cart line + upsell: full-width row at the bottom of the item (flex or grid) */
      .cd-cart-item:has(> .${SLOT}--line:not(:empty)),
      .cd-cart-upsell-item:has(> .${SLOT}--upsell:not(:empty)) {
        flex-wrap: wrap;
        row-gap: 8px;
      }
      /* Keep image + info on the first row once wrapping is enabled */
      .cd-cart-item:has(> .${SLOT}--line:not(:empty)) > .cd-cart-item-info,
      .cd-cart-upsell-item:has(> .${SLOT}--upsell:not(:empty)) > .cd-cart-upsell-item-info {
        flex: 1 1 0%;
        min-width: 0;
      }
      .${SLOT}--line,
      .${SLOT}--upsell {
        flex: 0 0 100%;
        width: 100%;
        min-width: 0;
        grid-column: 1 / -1;
      }
      /* Context-agnostic; also zeroes the card context's 5px left margin */
      .${SLOT}--line .ss-banner-wrapper,
      .${SLOT}--upsell .ss-banner-wrapper {
        width: 100%;
        margin: 0;
      }
      .${SLOT}--line .ss-banner-wrapper .ss-banner,
      .${SLOT}--upsell .ss-banner-wrapper .ss-banner {
        width: 100%;
      }
      .${SLOT}--line .ss-banner-wrapper .ss-banner-count,
      .${SLOT}--upsell .ss-banner-wrapper .ss-banner-count {
        flex: 1 1 auto;
      }

      /* Kaching bundles: entries multiplier badge beside the deal-bar title */
      .${SLOT}--kaching {
        display: inline-flex;
        vertical-align: middle;
        margin-left: 5px;
        max-width: 100%;
        flex: 0 0 auto;
      }
      /* Kaching injects \`.kaching-bundles * { border-radius: 0 !important }\` from a <style> inside
         <kaching-bundles-block>, i.e. later in the document than this head rule. A bare
         .ss-kaching-badge loses padding and text-transform to it on specificity, and border-radius
         to its !important - hence the extra scope and the one !important. Verified in-page. */
      .${SLOT}--kaching .ss-kaching-badge,
      .kaching-bundles .${SLOT}--kaching .ss-kaching-badge {
        display: inline-flex;
        align-items: center;
        flex: 0 0 auto;
        padding: 6px 12px;
        border-radius: 1.1rem !important;
        background: var(--ss-badge-bg, #000000);
        color: var(--ss-badge-text, #ffffff);
        font-size: 12px;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        white-space: nowrap;
      }
    `;
    document.head.appendChild(style);
  }

  /* ---------- scheduler + observer (Cartix) ---------- */

  let timer = 0;
  let running = false;
  let queued = false;

  function schedule(root) {
    clearTimeout(timer);
    timer = setTimeout(() => run(root), CONFIG.debounceMs);
  }

  async function run(root) {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      if (root.querySelector(CONFIG.drawer)) {
        syncUpsells(root);
        await syncLines(root);
        syncTotal(root);
        syncProgress(root);
      }
    } catch (err) {
      console.warn('[ss-cartix]', err);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        schedule(root);
      }
    }
  }

  const isOwnNode = (node) => {
    const el = node.nodeType === 1 ? node : node.parentElement;
    return !!(el && el.closest(`.${SLOT}`));
  };

  const isOwnMutation = (record) => {
    if (isOwnNode(record.target)) return true;
    if (record.type !== 'childList') return false;
    const nodes = [...record.addedNodes, ...record.removedNodes];
    return nodes.length > 0 && nodes.every(isOwnNode);
  };

  function waitFor(selector) {
    return new Promise((resolve) => {
      const found = document.querySelector(selector);
      if (found) return resolve(found);
      const mo = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          mo.disconnect();
          resolve(el);
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    });
  }

  function observe(root) {
    const mo = new MutationObserver((records) => {
      if (!root.isConnected) {
        mo.disconnect();
        boot();
        return;
      }
      if (records.every(isOwnMutation)) return;
      schedule(root);
    });
    mo.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-cart-item', 'data-cart-quantity', 'href'],
    });
    schedule(root);
  }

  async function boot() {
    const root = await waitFor(CONFIG.appRoot);
    observe(root);
  }

  /* ---------- bootstrap ---------- */

  injectBaseStyles();
  boot();
  bootKaching();
})();